import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import pageContentService, { PageContent } from '../services/pageContent';
import AuthenticatedImage from '../components/AuthenticatedImage';

const Information: React.FC = () => {
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      setLoading(true);
      const content = await pageContentService.getPageBySlug('information');
      setPageContent(content);
    } catch (error) {
      console.error('Error loading page content:', error);
      setError('Failed to load information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  const handleLinkClick = (url: string) => {
    // Handle internal navigation or external links
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // For internal links, you might want to use React Router navigation
      window.location.href = url;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="px-4 py-4">
            <button
              onClick={goBack}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </header>
        <div className="px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-6"></div>
            <div className="aspect-[16/6] md:aspect-[16/4] bg-gray-200 rounded-lg mb-8"></div>
            <div className="space-y-6">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="px-4 py-4">
            <button
              onClick={goBack}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </header>
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadPageContent}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!pageContent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="px-4 py-4">
            <button
              onClick={goBack}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </header>
        <div className="text-center py-12">
          <p className="text-gray-500">Information not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <button
            onClick={goBack}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      </header>

      {/* Page Content */}
      <div className="bg-white">
        {/* Title */}
        <div className="text-center py-6">
          <h1 className="text-3xl font-serif text-gray-900">Information</h1>
        </div>

        {/* Banner Image */}
        {pageContent.bannerImageUrl && (
          <div className="px-4 md:px-0 mb-8">
            <AuthenticatedImage
              src={`/api/page-content/banner/${pageContent.id}`}
              alt="Information banner"
              className="w-full h-auto rounded-lg md:rounded-none"
            />
          </div>
        )}

        {/* Sections */}
        {pageContent.sections && pageContent.sections.length > 0 && (
          <div className="px-4 pb-8 max-w-2xl mx-auto">
            {pageContent.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-8">
                {/* Section Header */}
                <h2 className="text-xl font-medium text-gray-900 text-center mb-4">
                  {section.header}
                </h2>
                
                {/* Section Links */}
                <div className={
                  section.header.toLowerCase().includes('package') && section.links.length === 2
                    ? "grid grid-cols-2 gap-3"
                    : "space-y-3"
                }>
                  {section.links.map((link, linkIndex) => (
                    <button
                      key={linkIndex}
                      onClick={() => handleLinkClick(link.url)}
                      className="w-full text-center py-3 px-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                      <span className="text-gray-700 font-medium">{link.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Information;